import { useId, type ReactNode } from "react";

import { TiendaNubeLogo } from "./BrandHeader";

// Full-color brand logos for the connector source marks (Fuentes tab + source
// rack). Inlined as SVG components — same pattern as <TiendaNubeLogo> — so they
// stay crisp at any size, carry no expiring CDN URL, and render on a transparent
// background (we place them on a light tinted chip, not a brand-color fill).

/**
 * The logo node for a connector kind, or `null` when we have no brand logo
 * (the caller then falls back to the kind's initials). Centralizes the
 * per-kind mapping so the source rack and the Fuentes stat cards stay in sync.
 */
export function providerLogoFor(kind: string, size = 20): ReactNode | null {
  switch (kind) {
    case "tiendanube":
      return <TiendaNubeLogo size={size} />;
    case "google_analytics_4":
      return <GoogleAnalyticsLogo size={size} />;
    case "meta_ads":
      return <MetaLogo size={size} />;
    default:
      return null;
  }
}

/** Google Analytics 4 mark — the three amber bars. Source: gstatic ic_analytics. */
export function GoogleAnalyticsLogo({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 192 192"
      role="img"
      aria-label="Google Analytics"
      data-testid="ga4-logo"
    >
      <path
        fill="#F9AB00"
        d="M130,29v132c0,14.77,10.19,23,21,23c10,0,21-7,21-23V30c0-13.54-10-22-21-22S130,17.33,130,29z"
      />
      <path
        fill="#E37400"
        d="M75,96v65c0,14.77,10.19,23,21,23c10,0,21-7,21-23V97c0-13.54-10-22-21-22S75,84.33,75,96z"
      />
      <circle fill="#E37400" cx="41" cy="163" r="21" />
    </svg>
  );
}

/** Meta mark — the infinity glyph only (no "Meta" wordmark), blue gradient. */
export function MetaLogo({ size = 20 }: { size?: number }) {
  // Unique gradient id per instance — two Meta marks can render on one page
  // (source rack + Fuentes card) and duplicate SVG ids are invalid.
  const gradientId = useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="img"
      aria-label="Meta"
      data-testid="meta-logo"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="24" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#1B9DF5" />
          <stop offset="1" stopColor="#0062E0" />
        </linearGradient>
      </defs>
      <path
        fill={`url(#${gradientId})`}
        d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.158-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.085-.288z"
      />
    </svg>
  );
}
